import React, { useState } from "react";
import {
  CalendarCheck,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mail,
  TriangleAlert,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useMe } from "../hooks/queries/useMe";
import { useMyPlan, usePlanCatalog } from "../hooks/queries/usePlans";
import { formatPlanDate, planContactHref } from "../constants/plans";

// "Mi plan" (DEV-43). Todo el contenido —precios, textos, funciones— viene del
// backend (GET /plans, GET /plans/me); la página solo lo presenta. Todavía no
// hay cobro: "Quiero este plan" abre un correo al equipo.

const AUDIENCE_LABEL = { athlete: "Deportista", scout: "Cazatalentos y entrenadores" };

const statusLine = (myPlan) => {
  if (!myPlan) return null;
  const until = formatPlanDate(myPlan.expires_at);
  switch (myPlan.status) {
    case "trial":
      return until ? `Tu prueba gratuita termina el ${until}.` : "Estás en tu prueba gratuita.";
    case "none":
      return "Sin plan. Puedes explorar perfiles y usar la búsqueda básica; elige un plan para desbloquear el resto.";
    case "default":
      return "Plan gratuito.";
    case "active":
      return until ? `Activo hasta el ${until}.` : "Plan activo.";
    default:
      return null;
  }
};

const CurrentPlan = ({ myPlan }) => {
  const plan = myPlan?.plan;
  const legal = myPlan?.legal;
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tu plan actual</p>
      <h2 className="mt-1 text-2xl font-bold text-gray-900" data-testid="current-plan-name">
        {plan ? plan.name : "Sin plan"}
      </h2>
      {statusLine(myPlan) && <p className="mt-1 text-sm text-gray-600">{statusLine(myPlan)}</p>}

      {legal?.booking_url && (
        <div className="mt-4 flex flex-col gap-3 rounded-xl bg-blue-50 p-4 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-start gap-3">
            <CalendarCheck size={20} className="mt-0.5 shrink-0 text-blue-900" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-blue-950">Asesoría jurídica deportiva</p>
              <p className="text-sm text-blue-900/80">
                {legal.per_month === 1 ? "1 incluida por mes" : `${legal.per_month} incluidas por mes`}
              </p>
            </div>
          </div>
          <a
            href={legal.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-lg bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
          >
            Agendar asesoría jurídica
          </a>
        </div>
      )}
    </section>
  );
};

const PlanCard = ({ plan, current, expanded, onToggle, contactHref }) => (
  <article
    data-testid={`plan-card-${plan.id}`}
    className={`flex flex-col rounded-2xl border bg-white p-5 shadow-sm ${
      current ? "border-blue-900 ring-1 ring-blue-900" : "border-gray-200"
    }`}
  >
    <div className="flex items-start gap-2">
      <div className="flex-1">
        <p className="text-lg font-bold text-gray-900">{plan.name}</p>
        <p className="text-2xl font-bold text-blue-900">{plan.price_label}</p>
      </div>
      {current && (
        <span className="rounded-full bg-blue-900 px-2.5 py-0.5 text-xs font-semibold text-white">Tu plan</span>
      )}
      {!current && plan.trial_label && (
        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
          {plan.trial_label}
        </span>
      )}
    </div>

    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="mt-3 flex items-center gap-1 self-start text-sm font-medium text-gray-600 underline"
    >
      {expanded ? "Ocultar detalles" : "Ver qué incluye"}
      {expanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
    </button>

    {expanded && (
      <div className="mt-3 text-sm text-gray-700">
        {plan.includes_label && <p className="mb-2 font-semibold text-gray-900">{plan.includes_label}</p>}
        <ul className="space-y-1.5">
          {(plan.highlights || []).map((item) => (
            <li key={item} className="flex items-start gap-2">
              <Check size={16} className="mt-0.5 shrink-0 text-blue-900" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        {plan.notice && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-xs text-red-700">
            <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <p>{plan.notice}</p>
          </div>
        )}
      </div>
    )}

    {!current && plan.price_cop !== 0 && (
      <a
        href={contactHref}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
      >
        <Mail size={16} aria-hidden="true" />
        {plan.contact_only ? "Contáctenos" : "Quiero este plan"}
      </a>
    )}
  </article>
);

const PlanPage = () => {
  const { userRole, athleteData, scoutData } = useAuth();
  const me = useMe();
  const myPlan = useMyPlan();
  const catalog = usePlanCatalog();
  const [expanded, setExpanded] = useState(null);

  const audience = myPlan.data?.role === "scout" || userRole === "scout" ? "scout" : "athlete";
  const plans = (catalog.data || []).filter((plan) => plan.audience === audience);
  const currentId = myPlan.data?.plan?.id ?? null;
  const contact = {
    fullName: (audience === "scout" ? scoutData : athleteData)?.full_name || me.data?.user?.full_name,
    email: me.data?.user?.email,
  };

  const loading = myPlan.isLoading || catalog.isLoading;
  const failed = myPlan.isError || catalog.isError;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 sm:px-6">
        <header>
          <h1 className="text-2xl font-bold text-gray-900">Mi plan</h1>
          <p className="text-sm text-gray-600">Planes para {AUDIENCE_LABEL[audience].toLowerCase()}.</p>
        </header>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-blue-900" size={28} aria-label="Cargando planes" />
          </div>
        ) : failed ? (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            No pudimos cargar los planes. Inténtalo de nuevo en unos minutos.
          </div>
        ) : (
          <>
            <CurrentPlan myPlan={myPlan.data} />
            <section aria-label="Planes disponibles" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  current={plan.id === currentId}
                  expanded={expanded === plan.id}
                  onToggle={() => setExpanded((open) => (open === plan.id ? null : plan.id))}
                  contactHref={planContactHref(plan, contact)}
                />
              ))}
            </section>
            <p className="text-xs text-gray-500">
              Para activar o cambiar tu plan, escríbenos y te ayudamos a hacerlo.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanPage;
